"use client"
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { handleNavigationWithPaywall } from "@/utils/paywall-navigation-utils";

import { ChevronRight, type LucideIcon } from "lucide-react"
import { isMenuItemActive, isGroupActive } from "@/utils/sidebar-utils"

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from "@/components/ui/sidebar"

function PolicyManagement({
    items,
}: {
    items: {
        title: string
        url: string
        icon: LucideIcon | ((props: any) => React.ReactElement)
        routeName?: string
        isActive?: boolean
        items?: {
            title: string
            url: string
            icon: LucideIcon | ((props: any) => React.ReactElement)
            routeName?: string
        }[]
    }[]
}) {
    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = location.pathname.substring(1); // Get current path without leading slash
    return (
        <SidebarGroup>
            <SidebarMenu>
                {items.map((item) => (
                    <Collapsible key={item.title} asChild defaultOpen={item.isActive || isGroupActive(item.items, currentPath)}>
                        <SidebarMenuItem>
                            {item.items?.length ? (
                                // Collapsible section: make the header (icon + text) a trigger
                                <CollapsibleTrigger asChild>
                                    <SidebarMenuButton asChild tooltip={item.title} isActive={isGroupActive(item.items, currentPath)}>
                                        <button type="button" className="w-full flex items-center gap-2 text-left" data-sentry-unmask>
                                            <item.icon className="text-nav-title" data-sentry-unmask/>
                                            <span className="text-nav-title" data-sentry-unmask>{item.title}</span>
                                        </button>
                                    </SidebarMenuButton>
                                </CollapsibleTrigger>
                            ) : (
                                // Non-collapsible: keep as link
                                <SidebarMenuButton asChild tooltip={item.title}>
                                    <button type="button" className="w-full flex items-center gap-2 text-left" onClick={(e) => {
                                        handleNavigationWithPaywall(item.url, navigate, e, item?.routeName);
                                    }}
                                    data-sentry-unmask>
    <item.icon data-sentry-unmask />
    <span data-sentry-unmask>{item.title}</span>
</button>
                                </SidebarMenuButton>
                            )}
                            {item.items?.length ? (
                                <>
                                    <CollapsibleTrigger asChild>
                                        <SidebarMenuAction className="data-[state=open]:rotate-90">
                                            <ChevronRight />
                                            <span className="sr-only">Toggle</span>
                                        </SidebarMenuAction>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <SidebarMenuSub>
                                            {item.items.map((subItem) => (
                                                <SidebarMenuSubItem key={subItem.title}>
                                                    <SidebarMenuSubButton asChild isActive={isMenuItemActive(subItem.url, currentPath)}>
                                                        <button type="button" className="w-full flex items-center gap-2 text-left" onClick={(e) => {
                                                            handleNavigationWithPaywall(subItem.url, navigate, e, subItem?.routeName);
                                                        }}
                                                        onAuxClick={(e) => {
                                                            // Middle click
                                                            handleNavigationWithPaywall(subItem.url, navigate, e, subItem?.routeName);
                                                        }}
                                                        onContextMenu={(e) => {
                                                            // Right click - prevent context menu and open in new tab
                                                            e.preventDefault();
                                                            handleNavigationWithPaywall(subItem.url, navigate, e, subItem?.routeName);
                                                        }}
                                                        data-sentry-unmask>
    <subItem.icon data-sentry-unmask />
    <span data-sentry-unmask>{subItem.title}</span>
</button>
                                                    </SidebarMenuSubButton>
                                                </SidebarMenuSubItem>
                                            ))}
                                        </SidebarMenuSub>
                                    </CollapsibleContent>
                                </>
                            ) : null}
                        </SidebarMenuItem>
                    </Collapsible>
                ))}
            </SidebarMenu>
        </SidebarGroup>
    )
}

export { PolicyManagement }
